import { Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { dataURLtoFile } from '@app/shared/helpers';
import { NgxGalleryImage, NgxGalleryImageSize, NgxGalleryOptions } from '@kolkov/ngx-gallery';
import { GiftCardStyle } from '@models/gift-cards/gift-card-style';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BlobService } from '@services/blob.service';
import { GiftCardsService } from '@services/gift-cards.service';
import { fabric } from 'fabric';
import { GiftCardImageSelectionComponent } from '../../gift-card-image-selection/gift-card-image-selection.component';

@Component({
  selector: 'app-gift-card-styles',
  templateUrl: './gift-card-styles.component.html',
  styleUrls: ['./gift-card-styles.component.less'],
})
export class GiftCardStylesComponent implements OnInit {
  @Input() parentLoading: boolean;
  @Input() readOnly = false;
  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChildren('thumbImg') thumbImgs: QueryList<ElementRef>;
  loading = false;
  addingImage: File;
  selectedIndex: number;
  styles: GiftCardStyle[] = [];
  // galleryImages: NgxGalleryImage[] = [];
  // defaultGalleryOptions: NgxGalleryOptions[] = [
  //   {
  //     height: '480px',
  //     preview: false,
  //     imagePercent: 65.4,
  //     imageSize: NgxGalleryImageSize.Contain,
  //     thumbnailSize: NgxGalleryImageSize.Contain,
  //     thumbnailsColumns: 4,
  //     thumbnailsPercent: 17.8,
  //     thumbnailsMoveSize: 3,
  //   },
  // ];
  // addingGalleryOptions: NgxGalleryOptions[] = [
  //   {
  //     height: '85px',
  //     preview: false,
  //     image: false,
  //     imageSize: NgxGalleryImageSize.Contain,
  //     thumbnailSize: NgxGalleryImageSize.Contain,
  //     thumbnailsColumns: 4,
  //     thumbnailsMoveSize: 3,
  //   },
  // ];

  private canvas: fabric.Canvas;
  private cardFrameRect: fabric.Rect;
  private readonly giftCardSize = { width: 320, height: 200 };

  constructor(
    private ngbModal: NgbModal,
    private matDialog: MatDialog,
    private giftCardService: GiftCardsService,
    private blobService: BlobService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.giftCardService.getGiftCardStyles().subscribe(
      (styles) => {
        const sas = this.blobService.getReadOnlySAS();
        this.styles = styles.reverse().map((style) => ({
          ...style,
          imageBlobUrl: style.imageBlobUrl + sas,
          thumbnailBlobUrl: style.thumbnailBlobUrl + sas,
        }));
        this.selectedIndex = 0;
        this.loading = false;
      },
      () => (this.loading = false)
    );
  }

  addStyleClick() {
    const modalRef = this.ngbModal.open(GiftCardImageSelectionComponent, {
      windowClass: 'add-photo-modal',
      centered: true,
    });
    modalRef.result.then((result: File) => {
      if (result) {
        this.addingImage = result;
        // setTimeout is used to wait for change detection
        setTimeout(() => this.setCanvasImage());
      }
    });
  }

  removeStyleClick() {
    const modalRef = this.matDialog.open(GenericDialogComponent, {
      width: '250px',
      data: {
        showCancel: true,
        title: 'Warning',
        content: 'Are you sure you want to remove this style? This action cannot be undone.',
        confirmButtonText: 'Yes',
      },
    });
    modalRef.afterClosed().subscribe((result) => {
      if (result === 'confirm') {
        this.archiveSelectedStyle();
      }
    });
  }

  nextStyle() {
    if (this.styles[this.selectedIndex + 1]) {
      this.selectedIndex = this.selectedIndex + 1;
      this.thumbImgs.get(this.selectedIndex).nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }

  previousStyle() {
    if (this.styles[this.selectedIndex - 1]) {
      this.selectedIndex = this.selectedIndex - 1;
      this.thumbImgs.get(this.selectedIndex).nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }

  selectStyle(i: number) {
    this.selectedIndex = i;
    this.thumbImgs.get(this.selectedIndex).nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  setCanvasImage() {
    if (!this.addingImage) return;
    const canvasEl = this.canvasRef.nativeElement;
    this.canvas = new fabric.Canvas(canvasEl, { backgroundColor: 'white' });
    const canvas = this.canvas;
    canvas.setDimensions({ width: this.giftCardSize.width * 2, height: this.giftCardSize.height * 2 });
    canvas.preserveObjectStacking = true;
    canvas.selection = false;
    const src = URL.createObjectURL(this.addingImage);

    fabric.Image.fromURL(src, (img) => {
      img.scaleToHeight(this.giftCardSize.height);
      img.left = this.giftCardSize.width - img.getScaledWidth() / 2;
      img.top = this.giftCardSize.height / 2;
      canvas.add(img);
      canvas.setActiveObject(img);
      img.sendToBack();
    });

    this.cardFrameRect = new fabric.Rect({
      left: this.giftCardSize.width / 2,
      top: this.giftCardSize.height / 2,
      width: this.giftCardSize.width,
      height: this.giftCardSize.height,
      fill: 'transparent',
      stroke: 'black',
      strokeWidth: 2,
      rx: 9,
      ry: 9,
      selectable: false,
      evented: false,
    });
    canvas.add(this.cardFrameRect);
  }

  onSave() {
    this.loading = true;
    this.canvas.remove(this.cardFrameRect);
    const dataUrl = this.canvas.toDataURL({
      format: 'jpeg',
      quality: 1,
      left: this.giftCardSize.width / 2,
      top: this.giftCardSize.height / 2,
      width: this.giftCardSize.width,
      height: this.giftCardSize.height,
    });
    const file = dataURLtoFile(dataUrl, this.addingImage.name);
    this.giftCardService.addGiftCardStyle(file).subscribe(
      (style) => {
        const sas = this.blobService.getReadOnlySAS();
        style.imageBlobUrl += sas;
        style.thumbnailBlobUrl += sas;
        this.styles.unshift(style);
        this.selectedIndex = 0;
        this.addingImage = null;
        this.loading = false;
      },
      () => {
        this.addingImage = null;
        this.loading = false;
      }
    );
  }

  onCancel() {
    this.addingImage = null;
  }

  selectedStyleChanged(event: { index: number; image: NgxGalleryImage }) {
    this.selectedIndex = event.index;
  }

  private archiveSelectedStyle() {
    if (this.selectedIndex == null) return;
    this.loading = true;
    const style = this.styles[this.selectedIndex];
    style.active = false;
    this.giftCardService.updateGiftCardStyle(style).subscribe(
      () => {
        this.styles.splice(this.selectedIndex, 1);
        this.selectedIndex = 0;
        this.loading = false;
      },
      () => {
        style.active = true;
        this.loading = false;
      }
    );
  }
}
